import React, { useEffect, useState, useRef, memo } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useAppContext } from '../../hooks/useAppContext';
import './styles.css';

const StatsSection = () => {
  const { language } = useAppContext();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  // Données des statistiques - Une seule couleur (charte graphique)
  const statsData = [
    {
      id: 1,
      icon: 'fa-user-plus',
      value: 598,
      label: language === 'ar' ? 'المسجلين' : 'Inscrits',
      description: language === 'ar' ? ' إجمالي المسجلين في البرنامج' : 'Total inscrits au programme avec un taux de réalisation de 58%'
    },
    {
      id: 2,
      icon: 'fa-running',
      value: 468,
      label: language === 'ar' ? 'في المسار' : 'En parcours',
      description: language === 'ar' ? 'يتابعون التكوين حاليا' : 'Suivent actuellement la formation'
    },
    {
      id: 3,
      icon: 'fa-graduation-cap',
      value: 317,
      label: language === 'ar' ? 'الخريجين' : 'Sortis',
      description: language === 'ar' ? 'أنهوا البرنامج بنجاح' : 'Ont terminé le programme'
    },
    {
      id: 4,
      icon: 'fa-user-tie',
      value: 153,
      label: language === 'ar' ? 'الخريجين' : 'Alumnis',
      description: language === 'ar' ? 'شبكة الخريجين النشطين' : 'Réseau d\'anciens actifs'
    }
  ];


  // Observer pour détecter quand la section est visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);


  return (
    <section className="stats-section" ref={sectionRef}>
      <div className="stats-container">
        <div className="section-header">
          <motion.h2 
            className="stats-title text-[#0f4c81]"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8 }}
          >
            {language === 'ar' ? 'إحصائيات البرنامج' : 'Nos Chiffres Clés'}
          </motion.h2>
          <motion.p 
            className="stats-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {language === 'ar' 
              ? 'تعرف على أرقام وإنجازات برنامج الفرصة الجديدة في نهاية 2025' 
              : 'Découvrez l\'impact du Dispositif Nouvelle Chance en chiffres en fin 2025.'}
          </motion.p>
        </div>

        <div className="stats-grid">
          {statsData.map((stat, index) => (
            <motion.div 
              key={stat.id}
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="stat-icon-container">
                <i className={`fas ${stat.icon}`}></i>
              </div>
              
              <div className="stat-value">
                {isVisible ? (
                  <CountUp 
                    end={stat.value} 
                    duration={2.5} 
                    separator=" "
                  />
                ) : (
                  <span>{stat.value}</span>
                )}
              </div>
              
              <h3 className="stat-label">
                {stat.label}
              </h3>
              
              <p className="stat-description">
                {stat.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Exporter le composant mémorisé pour éviter les rendus inutiles
export default memo(StatsSection);
